<template>
  <div class="task-filters">
    <!-- Filter Header -->
    <div class="task-filters__header">
      <div class="task-filters__title">
        <theme-icon icon="filter" type="icons" />
        <span>Filters</span>
      </div>
      <div class="task-filters__actions">
        <themed-badge v-if="activeFilterCount > 0" variant="secondary">
          {{ activeFilterCount }}
        </themed-badge>
        <themed-button
          v-if="activeFilterCount > 0"
          variant="ghost"
          size="sm"
          @click="clearAllFilters"
        >
          Clear All
        </themed-button>
      </div>
    </div>

    <!-- Filter Controls -->
    <div class="task-filters__controls">
      <!-- Status Filter -->
      <div class="task-filters__field">
        <label class="task-filters__label">Status</label>
        <themed-select
          v-model="localFilters.status_id"
          :options="statusOptions"
          placeholder="All Statuses"
          @input="onFilterChange"
        />
      </div>

      <!-- Priority Filter -->
      <div class="task-filters__field">
        <label class="task-filters__label">Priority</label>
        <themed-select
          v-model="localFilters.priority_id"
          :options="priorityOptions"
          placeholder="All Priorities"
          @input="onFilterChange"
        />
      </div>

      <!-- Assignee Filter -->
      <div class="task-filters__field">
        <label class="task-filters__label">Assignee</label>
        <vue-multiselect
          v-model="selectedAssignee"
          :options="assigneeGroups"
          group-values="items"
          group-label="group"
          :searchable="true"
          :multiple="false"
          :close-on-select="true"
          :clear-on-select="false"
          :allow-empty="true"
          :show-labels="false"
          track-by="key"
          label="label"
          placeholder="All Assignees"
          class="themed-multiselect"
          @select="onAssigneeSelect"
          @remove="onAssigneeClear"
        />
      </div>

      <!-- Project Filter -->
      <div v-if="!hiddenFields.includes('project')" class="task-filters__field">
        <label class="task-filters__label">Project</label>
        <themed-select
          v-model="localFilters.project"
          :options="projectOptions"
          placeholder="All Projects"
          @input="onFilterChange"
        />
      </div>

      <!-- Site Filter -->
      <div v-if="!hiddenFields.includes('site')" class="task-filters__field">
        <label class="task-filters__label">Site</label>
        <themed-select
          v-model="localFilters.site"
          :options="siteOptions"
          placeholder="All Sites"
          @input="onFilterChange"
        />
      </div>

      <!-- Due Date Range Filter -->
      <div class="task-filters__field">
        <label class="task-filters__label">Due Date From</label>
        <themed-date-picker
          :value="localFilters.due_date_from"
          placeholder="From"
          @input="localFilters.due_date_from = $event; onFilterChange()"
        />
      </div>

      <div class="task-filters__field">
        <label class="task-filters__label">Due Date To</label>
        <themed-date-picker
          :value="localFilters.due_date_to"
          placeholder="To"
          @input="localFilters.due_date_to = $event; onFilterChange()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VueMultiselect from 'vue-multiselect';
import ThemedSelect from '../ThemedSelect.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedBadge from '../ThemedBadge.vue';
import ThemeIcon from '../ThemeIcon.vue';
import ThemedDatePicker from '../form/ThemedDatePicker.vue';

export default {
  name: 'TaskFilters',

  components: {
    VueMultiselect,
    ThemedSelect,
    ThemedButton,
    ThemedBadge,
    ThemeIcon,
    ThemedDatePicker,
  },

  props: {
    value: {
      type: Object,
      default: () => ({
        status_id: null,
        priority_id: null,
        assignee_type: null,
        assignee_id: null,
        project: null,
        site: null,
        due_date_from: null,
        due_date_to: null,
      })
    },
    hiddenFields: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      localFilters: {
        status_id: null,
        priority_id: null,
        assignee_type: null,
        assignee_id: null,
        project: null,
        site: null,
        due_date_from: null,
        due_date_to: null,
      },
      selectedAssignee: null,
      assigneeGroups: [],
      projectOptions: [],
      siteOptions: [],
      loading: false
    };
  },

  computed: {
    ...mapGetters('tasks', ['activeStatuses', 'activePriorities']),

    statusOptions() {
      if (!this.activeStatuses || this.activeStatuses.length === 0) {
        return [];
      }
      return [
        { label: 'All Statuses', value: null },
        ...this.activeStatuses.map(status => ({
          label: status.name,
          value: status.id
        }))
      ];
    },

    priorityOptions() {
      if (!this.activePriorities || this.activePriorities.length === 0) {
        return [];
      }
      return [
        { label: 'All Priorities', value: null },
        ...this.activePriorities.map(priority => ({
          label: priority.name,
          value: priority.id
        }))
      ];
    },

    activeFilterCount() {
      let count = 0;
      if (this.localFilters.status_id) count++;
      if (this.localFilters.priority_id) count++;
      if (this.localFilters.assignee_type) count++;
      if (this.localFilters.project) count++;
      if (this.localFilters.site) count++;
      if (this.localFilters.due_date_from || this.localFilters.due_date_to) count++;
      return count;
    }
  },

  watch: {
    value: {
      handler(newValue) {
        this.localFilters = { ...newValue };
      },
      deep: true,
      immediate: true
    }
  },

  async mounted() {
    await this.loadFilterOptions();
  },

  methods: {
    async loadFilterOptions() {
      this.loading = true;
      try {
        await Promise.all([
          this.$store.dispatch('tasks/fetchStatuses'),
          this.$store.dispatch('tasks/fetchPriorities')
        ]);

        const [assigneesResponse, projectsResponse, sitesResponse] = await Promise.all([
          axios.get('/api/tasks/assignees'),
          axios.get('/api/projects/dropdown'),
          axios.get('/api/sites/dropdown'),
        ]);

        this.assigneeGroups = (assigneesResponse.data || []).map(group => ({
          group: group.group,
          items: (group.items || []).map(item => ({
            label: item.label,
            assignee_type: item.assignee_type,
            assignee_id: item.assignee_id,
            key: `${item.assignee_type}:${item.assignee_id}`
          }))
        }));

        this.projectOptions = [
          { label: 'All Projects', value: null },
          ...projectsResponse.data.map(project => ({
            label: project.name,
            value: project.id
          }))
        ];

        this.siteOptions = [
          { label: 'All Sites', value: null },
          ...sitesResponse.data.map(site => ({
            label: site.name,
            value: site.id
          }))
        ];
      } catch (error) {
        console.error('Error loading filter options:', error);
        this.$toast.error('Failed to load filter options');
      } finally {
        this.loading = false;
      }
    },

    onAssigneeSelect(selected) {
      this.localFilters.assignee_type = selected.assignee_type;
      this.localFilters.assignee_id = selected.assignee_id;
      this.onFilterChange();
    },

    onAssigneeClear() {
      this.localFilters.assignee_type = null;
      this.localFilters.assignee_id = null;
      this.onFilterChange();
    },

    onFilterChange() {
      this.$emit('input', { ...this.localFilters });
      this.$emit('filter-change', { ...this.localFilters });
    },

    clearAllFilters() {
      this.localFilters = {
        status_id: null,
        priority_id: null,
        assignee_type: null,
        assignee_id: null,
        project: null,
        site: null,
        due_date_from: null,
        due_date_to: null,
      };
      this.selectedAssignee = null;
      this.onFilterChange();
    }
  }
};
</script>
